import { IBlowDef, Segment } from './types';
import { seg } from './segments';
import { ParamReader } from './ParamReader';

export const dots: Record<number, IBlowDef> = {
    42: {
        name: 'Burn',
        wikiDesc: 'Burns at Rate% chance, dealing ATK%, flat, and/or max-HP% damage as fire damage ' +
            'every 2 seconds.',
        describe({ params }) {
            const nums = params.numbers();
            const rate = nums[0];
            if (isNaN(rate)) {
                return null;
            }
            const atkPct = nums[1] || 0;
            const flat = nums[2] || 0;
            const maxHpPct = nums[3] || 0;
            const parts: Segment[][] = [];
            if (atkPct) {
                parts.push([seg.pct(atkPct, 2, params.span(1) || undefined), ' of ATK']);
            }
            if (flat) {
                parts.push([seg.num(flat, params.span(2) || undefined), ' flat']);
            }
            if (maxHpPct) {
                parts.push([seg.pct(maxHpPct, 2, params.span(3) || undefined), " of target's max HP"]);
            }
            const out: Segment[] = ['Burn targets at a ', seg.pct(rate, 2, params.span(0) || undefined), ' chance'];
            if (parts.length) {
                out.push(', dealing ', seg.list(parts), ' as fire damage every 2s');
            }
            return out;
        },
    },
    44: {
        name: 'Poison',
        wikiDesc: 'Poisons at Rate% chance, dealing HP% and/or RawDamage damage every 2s.',
        describe({ params }) {
            const nums = params.numbers();
            const rate = nums[0];
            if (isNaN(rate)) {
                return null;
            }
            const maxHpPct = nums[1] || 0;
            const flat = nums[2] || 0;
            const parts: Segment[][] = [];
            if (maxHpPct) {
                parts.push([seg.pct(maxHpPct, 2, params.span(1) || undefined), " of target's max HP"]);
            }
            if (flat) {
                parts.push([seg.num(flat, params.span(2) || undefined), ' flat']);
            }
            const out: Segment[] = [
                'Poisons targets at a ', seg.pct(rate, 2, params.span(0) || undefined), ' chance',
            ];
            if (parts.length) {
                out.push(', dealing ', seg.list(parts), ' as poison damage every 2s');
            }
            return out;
        },
    },
    115: {
        name: 'Poison detonate',
        wikiDesc: "Requires and consumes poison (44) or plague (136) on the target, then replays the " +
            "target's own Hit_Poison action's attack signals as the explosion. Does nothing if the " +
            "target's act has no such action.",
        note: 'The parameter has no effect.',
        describe() {
            return [
                'Detonates poison (', seg.blow(44), ') or plague (', seg.blow(136),
                ") on the target, replaying its own Hit_Poison action's attack signals as the explosion",
            ];
        },
    },
    116: {
        name: 'Burn detonate',
        wikiDesc: "Requires and consumes burn (42), then replays the target's Hit_Burn action's attack " +
            'signals.',
        note: 'The parameter has no effect.',
        describe() {
            return [
                'Detonates burn (', seg.blow(42),
                ") on the target, replaying its Hit_Burn action's attack signals",
            ];
        },
    },
    117: {
        name: 'Freeze detonate',
        wikiDesc: "Requires and consumes freeze (41) or frostbite (144), then replays the target's " +
            "Hit_Freezing action's attack signals.",
        note: 'The parameter has no effect.',
        describe() {
            return [
                'Detonates freeze (', seg.blow(41), ') or frostbite (', seg.blow(144),
                ") on the target, replaying its Hit_Freezing action's attack signals",
            ];
        },
    },
    118: {
        name: 'Electric Detonate',
        wikiDesc: "Requires and consumes electrocute (43), then replays the target's Hit_Shock action's " +
            'attack signals.',
        note: 'The parameter has no effect.',
        describe() {
            return [
                'Detonates electrocute (', seg.blow(43),
                ") on the target, replaying its Hit_Shock action's attack signals",
            ];
        },
    },
    136: {
        name: 'Plague',
        wikiDesc: 'DOT ticking every 2s for LastHitDamage * DamageRatio + Absolute; each tick rolls ' +
            "SpreadProb to jump to the nearest actor on the victim's team within Range, copying the " +
            'damage and full duration. DamageRatio >= 1.0 is treated as flat damage instead, so keep it ' +
            'below 1.',
        describe({ params }) {
            const f = params.numbers();
            // DamageRatio >= 1.0 is treated by the engine as flat damage instead of a ratio.
            const dmg = (ratio: number, span?: [number, number]): Segment[] => (ratio >= 1
                ? [seg.num(ratio, span), ' flat damage']
                : [seg.pct(ratio, 2, span), " of the last hit's damage"]);
            if (f.length <= 2) {
                const damageRatio = f[0];
                const range = f[1];
                if (isNaN(damageRatio) || isNaN(range)) {
                    return null;
                }
                const short: Segment[] = ['Ticks for '];
                return short.concat(dmg(damageRatio, params.span(0) || undefined), [
                    ' every 2s, spreading to the nearest ally within ',
                    seg.num(range, params.span(1) || undefined), ' range',
                ]);
            }
            const spreadProb = f[0];
            const damageRatio = f[1];
            const absolute = f[2] || 0;
            const range = f[3];
            if (isNaN(spreadProb) || isNaN(damageRatio) || isNaN(range)) {
                return null;
            }
            const out: Segment[] = (['Ticks for '] as Segment[]).concat(
                dmg(damageRatio, params.span(1) || undefined));
            if (absolute) {
                out.push(' + ', seg.num(absolute, params.span(2) || undefined), ' flat');
            }
            out.push(
                ' every 2s, ', seg.pct(spreadProb, 2, params.span(0) || undefined),
                ' chance to spread to the nearest ally within ',
                seg.num(range, params.span(3) || undefined), ' range',
            );
            const flags: string[] = [];
            if (f[4] === 1) {
                flags.push('can re-infect');
            }
            if (f[5] === 1) {
                flags.push('ignores summons');
            }
            if (f[6] === 1) {
                flags.push('ticks immediately');
            }
            if (flags.length) {
                out.push(' (', flags.join(', '), ')');
            }
            return out;
        },
    },
    157: {
        name: 'DOT (bleeding)',
        wikiDesc: "Bleed sized from the attacker's stats at apply: total damage = attacker's average " +
            'ATK (physical or magical, per the triggering hit) * DamageRatio, split evenly over the ' +
            'duration and ticking every IntervalTime. The first tick fires immediately; remaining ticks ' +
            'are paid out as a lump sum if the bleed is removed early.',
        describe({ params }) {
            const f = params.numbers();
            const ratio = f[0];
            const intervalMs = f[1];
            if (isNaN(ratio) || isNaN(intervalMs)) {
                return null;
            }
            return [
                'Bleeds for ', seg.pct(ratio), " of the attacker's average ATK, split evenly and " +
                'ticking every ', seg.secs(intervalMs),
            ];
        },
    },
    170: {
        name: 'Timed Explosion',
        wikiDesc: "If the skill hits the target, when this effect's duration expires, everything " +
            'hostile to the caster within Radius of the target (including the target) takes flat Damage.',
        describe({ params }) {
            const f = params.numbers();
            const radius = f[0];
            const damage = f[1];
            if (isNaN(radius) || isNaN(damage)) {
                return null;
            }
            return [
                'When the effect expires, deals ', seg.num(damage),
                ' flat damage to everything hostile to the caster within ', seg.num(radius),
                ' of the target',
            ];
        },
    },
    236: {
        name: 'Variance Damage',
        wikiDesc: 'Deals damage to the holder scaled by how many actors the parent skill hit ("raid ' +
            'spread" mechanic). Type 1 = Damage/hitCount; 2 = 0 if hitCount >= Count else ' +
            'Damage*hitCount; 3 = 0 if hitCount <= Count else Damage*hitCount; 4 = if hitCount < Count, ' +
            "deals Damage*(Count-hitCount) (or blow 16's HP% damage if Damage is 0), plus SubSkillID's " +
            'effects, otherwise shows an immune popup.',
        describe({ params }) {
            const f = params.fields();
            const damage = ParamReader.num(f[0]);
            const count = ParamReader.num(f[1]);
            const type = ParamReader.num(f[2]);
            if (isNaN(damage) || isNaN(count) || isNaN(type)) {
                return null;
            }
            const TYPE_LABELS = [
                '(type 0)',
                'Damage ÷ hit count',
                '0 if hit count ≥ Count, else Damage × hit count',
                '0 if hit count ≤ Count, else Damage × hit count',
                'scaled deficit damage plus a sub-skill',
            ];
            const out: Segment[] = [
                'Deals damage to the holder scaled by how many actors the parent skill hit: ',
                seg.en(type, TYPE_LABELS, 'unknown'),
                ' (Damage=', seg.num(damage), ', Count=', seg.num(count), ')',
            ];
            if (type === 4) {
                const hpRatio = ParamReader.num(f[3]);
                const killing = f[4] === '1';
                const subSkillId = ParamReader.num(f[5]);
                out.push('. If under Count hits, deals ', seg.num(damage), ' × (Count − hits)');
                if (damage === 0 && !isNaN(hpRatio)) {
                    out.push(' (or ', seg.pct(hpRatio), " of the target's max HP via ", seg.blow(16), ')');
                }
                if (!isNaN(subSkillId)) {
                    out.push(', plus ', seg.skill(subSkillId), "'s effects");
                }
                if (killing) {
                    out.push(' (can kill)');
                }
                out.push('; otherwise shows an immune popup');
            }
            return out;
        },
    },
    244: {
        name: 'Curse',
        wikiDesc: 'Damages the target once every given interval while cursed; all attribute defense is ' +
            'reduced by a given rate% via 306.',
        describe({ params }) {
            const f = params.numbers();
            const damageScaling = f[0];
            const flatDamage = f[1];
            const cooldownMs = f[2];
            const se306Param = f[3];
            if (isNaN(damageScaling) || isNaN(flatDamage) || isNaN(cooldownMs)) {
                return null;
            }
            const out: Segment[] = [
                'Every ', seg.secs(cooldownMs), ', deals ', seg.pct(damageScaling),
                ' scaled damage plus ', seg.num(flatDamage), ' flat damage',
            ];
            if (!isNaN(se306Param)) {
                out.push(', reducing all attribute defense (via ', seg.blow(306), ', param ',
                    seg.num(se306Param), ')');
            }
            return out;
        },
    },
    245: {
        name: 'Curse Breakdown',
        wikiDesc: "Requires Curse (244) on the target. Replaces the target's MATK with the caster's " +
            "and replays the target's own Hit_Curse action signals against itself and its allies; " +
            "duration is forced to that action's length. Refuses to begin if the target has no " +
            'Hit_Curse action.',
        note: 'No parameters — pass 0.',
        describe() {
            return [
                'Requires ', seg.blow(244), " on the target. Replaces the target's MATK with the " +
                "caster's and replays the target's own Hit_Curse action signals against itself and its " +
                'allies (refuses to begin if the target has no Hit_Curse action; duration is forced to ' +
                "that action's length)",
            ];
        },
    },
    325: {
        name: 'Bleed',
        wikiDesc: 'Causes bleeding at Rate% chance, dealing Coefficient% of ATK every 2 seconds.',
        note: 'Trailing params (0;1;1) are undocumented.',
        describe({ params }) {
            const nums = params.numbers();
            const rate = nums[0];
            const coeff = nums[1];
            if (isNaN(rate) || isNaN(coeff)) {
                return null;
            }
            return [
                'Causes bleeding at a ', seg.pct(rate, 2, params.span(0) || undefined), ' chance, dealing ',
                seg.pct(coeff, 2, params.span(1) || undefined), ' of ATK every 2 seconds',
            ];
        },
    },
    344: {
        name: 'Condition Dot Damage',
        wikiDesc: "Snapshots the caster's max attack (per Type) on apply and deals TotalPercent% of it " +
            'split evenly over Count ticks, one every Tick ms; ends itself after the last tick. Type: ' +
            '`sum` = PATK+MATK, `high` = highest of the two, `phy`, `mag`.',
        describe({ raw }) {
            const f = raw.split(';').map((s) => s.trim());
            if (f.length < 4) {
                return null;
            }
            const type = f[0].toLowerCase();
            const totalPercent = ParamReader.num(f[1]);
            const tickMs = ParamReader.num(f[2]);
            const count = ParamReader.num(f[3]);
            if (!type || isNaN(totalPercent) || isNaN(tickMs) || isNaN(count)) {
                return null;
            }
            const TYPE_LABELS: { [key: string]: string } = {
                sum: 'PATK + MATK', high: 'the higher of PATK/MATK', phy: 'PATK', mag: 'MATK',
            };
            const label = TYPE_LABELS[type] || type;
            return [
                'Snapshots ', seg.val(label), ' on apply and deals ', seg.pct(totalPercent),
                ' of it split evenly over ', seg.num(count), ' ticks, one every ', seg.secs(tickMs),
                ' (ends after the last tick)',
            ];
        },
    },
};
